/* ==========================================================================
   RIWAAYAT — Motion engine
   GSAP + ScrollTrigger + Lenis (loaded from CDN, deferred, before this file).
   Every effect degrades to static, visible content if a library is missing
   or the visitor prefers reduced motion.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var isDesktop = function () { return window.matchMedia('(min-width: 990px)').matches; };
  var hasGsap = typeof window.gsap !== 'undefined';
  var gsap = window.gsap;
  var ST = window.ScrollTrigger;
  var designMode = root.classList.contains('shopify-design-mode');

  if (hasGsap && ST) gsap.registerPlugin(ST);

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /* Show everything immediately when we can't animate */
  function revealAllStatic() {
    $$('[data-rw-reveal],[data-rw-clip]').forEach(function (el) {
      el.style.opacity = 1; el.style.clipPath = 'none'; el.style.transform = 'none';
    });
  }
  if (!hasGsap || !ST || reduce) {
    root.classList.remove('rw-js');
    revealAllStatic();
  }

  /* ------------------------------------------------------------------------
     Smooth scroll (desktop, fine pointer only — native on touch)
     ------------------------------------------------------------------------ */
  var lenis = null;
  if (hasGsap && ST && !reduce && finePointer && typeof window.Lenis !== 'undefined' && !designMode) {
    lenis = new window.Lenis({
      duration: 1.25,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      wheelMultiplier: 0.9,
      smoothWheel: true,
    });
    lenis.on('scroll', ST.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
  }
  window.rwLenis = lenis;
  window.rwLock = function (on) {
    document.body.classList.toggle('rw-locked', !!on);
    if (lenis) on ? lenis.stop() : lenis.start();
  };

  /* ------------------------------------------------------------------------
     Split text → word masks
     ------------------------------------------------------------------------ */
  function splitWords(el) {
    if (el.dataset.rwSplitDone) return $$('.rw-split-inner', el);
    var walk = function (node) {
      var out = [];
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var parts = child.textContent.split(/(\s+)/);
          var frag = document.createDocumentFragment();
          parts.forEach(function (p) {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
            var mask = document.createElement('span');
            mask.className = 'rw-wmask';
            var inner = document.createElement('span');
            inner.className = 'rw-split-inner';
            inner.textContent = p;
            mask.appendChild(inner);
            frag.appendChild(mask);
            out.push(inner);
          });
          child.parentNode.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          out = out.concat(walk(child));
        }
      });
      return out;
    };
    var words = walk(el);
    el.dataset.rwSplitDone = '1';
    return words;
  }

  function wrapWordsPlain(el) {
    if (el.dataset.rwWordsDone) return $$('.rw-word', el);
    var text = el.textContent.trim().split(/\s+/);
    el.innerHTML = text.map(function (w) { return '<span class="rw-word">' + w + '</span>'; }).join(' ');
    el.dataset.rwWordsDone = '1';
    return $$('.rw-word', el);
  }

  /* ------------------------------------------------------------------------
     Preloader — once per session
     ------------------------------------------------------------------------ */
  function runPreloader(done) {
    var pre = $('.rw-preloader');
    var seen = false;
    try { seen = sessionStorage.getItem('rw-intro') === '1'; } catch (e) {}
    if (!pre || seen || designMode || !hasGsap || reduce) {
      if (pre) pre.hidden = true;
      done(); return;
    }
    try { sessionStorage.setItem('rw-intro', '1'); } catch (e) {}
    window.rwLock(true);
    gsap.timeline({ onComplete: function () { pre.hidden = true; window.rwLock(false); } })
      .from($('.rw-preloader__logo', pre), { opacity: 0, scale: 0.92, filter: 'blur(8px)', duration: 1.4, ease: 'power3.out' }, 0.1)
      .from($('.rw-preloader__sub', pre), { opacity: 0, duration: 1, ease: 'power2.out' }, 0.9)
      .to(pre, { opacity: 0, duration: 0.9, ease: 'power2.inOut' }, 2.15)
      .add(done, 2.25);
  }

  /* ------------------------------------------------------------------------
     Header — hide on scroll down, solid after threshold
     ------------------------------------------------------------------------ */
  function initHeader() {
    var header = $('.rw-header');
    if (!header) return;
    var lastY = window.scrollY;
    var ticking = false;
    var announce = $('.rw-announce');
    var update = function () {
      var y = window.scrollY;
      var annH = announce ? announce.offsetHeight : 0;
      header.style.setProperty('--rw-header-top', Math.max(0, annH - y) + 'px');
      header.classList.toggle('is-scrolled', y > 40);
      var menuOpen = document.body.classList.contains('rw-locked');
      if (!menuOpen) header.classList.toggle('is-hidden', y > 320 && y > lastY + 2);
      if (y < lastY - 2) header.classList.remove('is-hidden');
      lastY = y; ticking = false;
    };
    window.addEventListener('scroll', function () { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------------
     Fullscreen menu
     ------------------------------------------------------------------------ */
  function initMenu() {
    var menu = $('.rw-menu');
    if (!menu) return;
    var curtain = $('.rw-menu__curtain', menu);
    var panel = $('.rw-menu__panel', menu);
    var openers = $$('[data-rw-menu-open]');
    var closers = $$('[data-rw-menu-close]', menu);
    var items = $$('[data-rw-menu-anim], .rw-mi', menu);
    var rows = $$('.rw-mi', menu);
    var groups = $$('.rw-menu__group', menu);
    var imgs = $$('.rw-menu__img', menu);
    var isOpen = false;
    var desktop = function () { return window.matchMedia('(min-width: 990px)').matches; };

    function setActive(idx) {
      rows.forEach(function (r) { r.classList.toggle('is-active', r.getAttribute('data-index') === idx); });
      groups.forEach(function (g) { g.classList.toggle('is-active', g.getAttribute('data-index') === idx); });
      imgs.forEach(function (im) { im.classList.toggle('is-active', im.getAttribute('data-index') === idx); });
    }
    function open() {
      if (isOpen) return; isOpen = true;
      menu.classList.add('is-open'); window.rwLock(true);
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      var def = menu.getAttribute('data-default');
      if (def && def !== '0') setActive(def);
      if (!hasGsap || reduce) { panel.style.clipPath = 'none'; return; }
      gsap.timeline()
        .fromTo(curtain, { yPercent: -101 }, { yPercent: 0, duration: 0.55, ease: 'power4.inOut' })
        .fromTo(panel, { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 0.75, ease: 'power4.inOut' }, 0.22)
        .fromTo(items, { yPercent: 60, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.7, stagger: 0.04, ease: 'power3.out' }, 0.55);
    }
    function close() {
      if (!isOpen) return; isOpen = false;
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      var finish = function () { menu.classList.remove('is-open'); window.rwLock(false); rows.forEach(function (r) { r.classList.remove('is-open'); }); };
      if (!hasGsap || reduce) { finish(); return; }
      gsap.timeline({ onComplete: finish })
        .to(panel, { clipPath: 'inset(100% 0 0% 0)', duration: 0.7, ease: 'power4.inOut' })
        .to(curtain, { yPercent: 101, duration: 0.55, ease: 'power4.inOut' }, 0.2)
        .set(curtain, { yPercent: -101 });
    }
    openers.forEach(function (b) { b.addEventListener('click', open); });
    closers.forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    rows.forEach(function (row) {
      var link = $('.rw-mi__link', row);
      var idx = row.getAttribute('data-index');
      row.addEventListener('mouseenter', function () { if (desktop()) setActive(idx); });
      link.addEventListener('focus', function () { setActive(idx); });
      if (link.tagName === 'BUTTON') {
        link.addEventListener('click', function () {
          if (desktop()) { setActive(idx); return; }
          var on = !row.classList.contains('is-open');
          rows.forEach(function (r) { r.classList.remove('is-open'); $('.rw-mi__link', r).setAttribute('aria-expanded', 'false'); });
          row.classList.toggle('is-open', on);
          link.setAttribute('aria-expanded', on ? 'true' : 'false');
        });
      }
    });
  }

  /* ------------------------------------------------------------------------
     Search overlay with predictive results
     ------------------------------------------------------------------------ */
  function initSearch() {
    var s = $('.rw-search');
    if (!s) return;
    var input = $('.rw-search__input', s);
    var results = $('.rw-search__results', s);
    var t;
    function open() {
      s.classList.add('is-open'); window.rwLock(true);
      requestAnimationFrame(function () { s.classList.add('is-visible'); setTimeout(function () { input && input.focus(); }, 250); });
    }
    function close() {
      s.classList.remove('is-visible'); window.rwLock(false);
      setTimeout(function () { s.classList.remove('is-open'); }, 500);
    }
    $$('[data-rw-search-open]').forEach(function (b) { b.addEventListener('click', function (e) { e.preventDefault(); open(); }); });
    $$('[data-rw-search-close]', s).forEach(function (b) { b.addEventListener('click', close); });
    s.addEventListener('click', function (e) { if (e.target === s) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && s.classList.contains('is-open')) close(); });
    if (!input || !results) return;
    input.addEventListener('input', function () {
      clearTimeout(t);
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      t = setTimeout(function () {
        fetch((window.rwRoutes && window.rwRoutes.predictive || '/search/suggest') + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=8')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            results.innerHTML = products.map(function (p) {
              var img = p.featured_image && p.featured_image.url ? p.featured_image.url + (p.featured_image.url.indexOf('?') > -1 ? '&' : '?') + 'width=400' : '';
              return '<a class="rw-search__result" href="' + p.url + '">' + (img ? '<img loading="lazy" src="' + img + '" alt="">' : '') + '<div>' + p.title + '</div><div class="rw-card__meta">' + (window.rwMoney ? window.rwMoney(Math.round(parseFloat(p.price) * 100)) : p.price) + '</div></a>';
            }).join('') || '<p>No pieces found — try “ring”, “anklet” or “pendant”.</p>';
          })
          .catch(function () {});
      }, 220);
    });
  }

  /* ------------------------------------------------------------------------
     Hero
     ------------------------------------------------------------------------ */
  function initHero() {
    $$('[data-rw-vitrine]').forEach(function (v) {
      if (v.dataset.rwDone) return; v.dataset.rwDone = '1';
      var pieces = $$('.rw-vitrine__piece', v);
      var caps = $$('.rw-vitrine__cap', v);
      var dots = $$('.rw-vitrine__dot', v);
      var copy = $('.rw-vitrine__copy', v);
      var light = $('.rw-vitrine__light', v);
      var ms = parseInt(v.getAttribute('data-interval') || '6000', 10);
      v.style.setProperty('--rw-vitrine-ms', ms + 'ms');
      var idx = 0, timer;
      function show(i) {
        if (!pieces.length) return;
        idx = (i + pieces.length) % pieces.length;
        pieces.forEach(function (p, j) { p.classList.toggle('is-active', j === idx); });
        caps.forEach(function (c, j) { c.hidden = j !== idx; });
        dots.forEach(function (d, j) { d.classList.remove('is-active'); if (j === idx) { void d.offsetWidth; d.classList.add('is-active'); } });
        clearTimeout(timer);
        if (!reduce && pieces.length > 1) timer = setTimeout(function () { show(idx + 1); }, ms);
      }
      dots.forEach(function (d, j) { d.addEventListener('click', function () { show(j); }); });
      show(0);

      /* Cursor spotlight */
      if (finePointer && light) {
        v.addEventListener('mousemove', function (e) {
          var r = v.getBoundingClientRect();
          v.style.setProperty('--lx', ((e.clientX - r.left) / r.width * 100) + '%');
          v.style.setProperty('--ly', ((e.clientY - r.top) / r.height * 100) + '%');
        });
      }

      /* Starlight canvas */
      var canvas = $('.rw-vitrine__stars', v);
      if (canvas && !reduce) {
        var ctx = canvas.getContext('2d'), stars = [], w, h, dpr = Math.min(window.devicePixelRatio || 1, 2);
        var size = function () {
          w = v.clientWidth; h = v.clientHeight;
          canvas.width = w * dpr; canvas.height = h * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          var n = Math.round((w * h) / 9000);
          stars = [];
          for (var k = 0; k < n; k++) stars.push({ x: Math.random() * w, y: Math.random() * h, r: Math.random() * 1.1 + 0.2, p: Math.random() * Math.PI * 2, s: 0.004 + Math.random() * 0.012 });
        };
        size(); window.addEventListener('resize', size);
        var visible = true;
        if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }).observe(v);
        var draw = function () {
          if (visible) {
            ctx.clearRect(0, 0, w, h);
            for (var k = 0; k < stars.length; k++) {
              var st = stars[k]; st.p += st.s;
              var a = 0.15 + (Math.sin(st.p) + 1) * 0.35;
              ctx.beginPath(); ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
              ctx.fillStyle = 'rgba(232,206,140,' + a.toFixed(3) + ')'; ctx.fill();
            }
          }
          requestAnimationFrame(draw);
        };
        draw();
      }

      if (!hasGsap || reduce) return;
      if (copy) { copy.style.animation = 'none'; copy.style.opacity = 1; }
      gsap.timeline({ delay: 0.1 })
        .fromTo($('.rw-vitrine__stage', v), { opacity: 0, scale: 0.92 }, { opacity: 1, scale: 1, duration: 2.2, ease: 'power3.out' }, 0)
        .fromTo($('.rw-vitrine__kicker', v), { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out' }, 0.3)
        .fromTo($$('.rw-hero__sub, .rw-hero__ctas > *, .rw-vitrine__foot', v), { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 1.3, ease: 'power3.out', stagger: 0.12 }, 0.9);
      if (ST) gsap.to($('.rw-vitrine__stage', v), { yPercent: -14, ease: 'none', scrollTrigger: { trigger: v, start: 'top top', end: 'bottom top', scrub: true } });
    });
  }

  /* ------------------------------------------------------------------------
     Generic reveals
     ------------------------------------------------------------------------ */
  function initReveals() {
    if (!hasGsap || !ST || reduce) return;

    $$('[data-rw-split]').forEach(function (el) {
      var words = splitWords(el);
      gsap.fromTo(words, { yPercent: 110 }, {
        yPercent: 0, duration: 1.1, ease: 'power4.out', stagger: 0.045,
        scrollTrigger: { trigger: el, start: 'top 85%', once: true },
      });
    });

    $$('[data-rw-reveal]').forEach(function (el) {
      var type = el.getAttribute('data-rw-reveal') || 'up';
      var from = { opacity: 0 };
      if (type === 'up') from.y = 50;
      if (type === 'left') from.x = -50;
      if (type === 'right') from.x = 50;
      if (type === 'scale') from.scale = 0.92;
      var delay = parseFloat(el.getAttribute('data-rw-delay') || 0);
      gsap.fromTo(el, from, {
        opacity: 1, x: 0, y: 0, scale: 1, duration: 1.2, delay: delay, ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      });
    });

    $$('[data-rw-stagger]').forEach(function (wrap) {
      var kids = Array.prototype.slice.call(wrap.children);
      gsap.fromTo(kids, { opacity: 0, y: 60 }, {
        opacity: 1, y: 0, duration: 1, ease: 'power3.out', stagger: 0.09,
        scrollTrigger: { trigger: wrap, start: 'top 85%', once: true },
      });
    });

    $$('[data-rw-clip]').forEach(function (el) {
      var img = $('img', el);
      var tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 85%', once: true } });
      tl.fromTo(el, { clipPath: 'inset(100% 0 0 0)' }, { clipPath: 'inset(0% 0 0 0)', duration: 1.4, ease: 'power4.inOut' });
      if (img) tl.fromTo(img, { scale: 1.3 }, { scale: 1, duration: 1.8, ease: 'power3.out' }, 0);
    });

    $$('[data-rw-parallax]').forEach(function (el) {
      var amt = parseFloat(el.getAttribute('data-rw-parallax')) || 12;
      gsap.fromTo(el, { yPercent: -amt }, { yPercent: amt, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
    });

    /* Count-up numbers */
    $$('[data-rw-count]').forEach(function (el) {
      var end = parseFloat(el.getAttribute('data-rw-count'));
      if (isNaN(end)) return;
      var suffix = el.getAttribute('data-rw-suffix') || '';
      var obj = { v: 0 };
      ST.create({
        trigger: el, start: 'top 90%', once: true,
        onEnter: function () {
          gsap.to(obj, { v: end, duration: 2, ease: 'power2.out', onUpdate: function () { var n = Math.round(obj.v); el.textContent = (end >= 10000 ? n.toLocaleString('en-IN') : n) + suffix; } });
        },
      });
    });

    /* Line-drawn SVG icons */
    $$('[data-rw-draw]').forEach(function (svg) {
      var paths = $$('path, circle, line, polyline, rect, ellipse', svg);
      paths.forEach(function (p) {
        var len = p.getTotalLength ? p.getTotalLength() : 200;
        p.style.strokeDasharray = len; p.style.strokeDashoffset = len;
      });
      gsap.to(paths, { strokeDashoffset: 0, duration: 1.6, ease: 'power2.inOut', stagger: 0.12, scrollTrigger: { trigger: svg, start: 'top 90%', once: true } });
    });
  }

  /* ------------------------------------------------------------------------
     Velocity marquee
     ------------------------------------------------------------------------ */
  function initMarquees() {
    $$('.rw-marquee').forEach(function (m) {
      var track = $('.rw-marquee__track', m);
      if (!track) return;
      if (!hasGsap || reduce) return;
      var dir = m.getAttribute('data-direction') === 'right' ? 1 : -1;
      var speed = parseFloat(m.getAttribute('data-speed') || 1);
      var x = 0, boost = 0, skew = 0;
      var half = function () { return track.scrollWidth / 2; };
      gsap.ticker.add(function (t, dt) {
        var v = (0.045 * speed + boost) * dt * dir;
        x += v;
        var w = half();
        if (dir < 0 && x <= -w) x += w;
        if (dir > 0 && x >= 0) x -= w;
        boost *= 0.92;
        skew *= 0.9;
        track.style.transform = 'translate3d(' + x + 'px,0,0) skewX(' + skew.toFixed(2) + 'deg)';
      });
      if (dir > 0) x = -half();
      if (ST) {
        ST.create({
          trigger: m, start: 'top bottom', end: 'bottom top',
          onUpdate: function (self) {
            var vel = self.getVelocity();
            boost = Math.min(Math.abs(vel) / 6000, 0.6);
            skew = gsap.utils.clamp(-8, 8, vel / -300);
          },
        });
      }
    });
  }

  /* ------------------------------------------------------------------------
     Five elements — pinned horizontal scroll (desktop)
     ------------------------------------------------------------------------ */
  /* ------------------------------------------------------------------------
     Hero — the salon (curtain-wipe slides, shoppable caption)
     ------------------------------------------------------------------------ */
  function initSalon() {
    $$('[data-rw-salon]').forEach(function (v) {
      if (v.dataset.rwDone) return; v.dataset.rwDone = '1';
      var slides = $$('.rw-salon__slide', v);
      var caps = $$('.rw-salon__cap', v);
      var bars = $$('.rw-salon__bar', v);
      var num = $('[data-rw-salon-num]', v);
      var ms = parseInt(v.getAttribute('data-interval') || '6000', 10);
      v.style.setProperty('--rw-salon-ms', ms + 'ms');
      var idx = 0, timer;
      function show(i) {
        if (!slides.length) return;
        var next = (i + slides.length) % slides.length;
        if (next !== idx) {
          slides.forEach(function (s, j) { s.classList.toggle('is-prev', j === idx); });
        }
        idx = next;
        slides.forEach(function (s, j) { s.classList.toggle('is-active', j === idx); });
        caps.forEach(function (c, j) { c.classList.toggle('is-active', j === idx); });
        bars.forEach(function (b, j) {
          b.classList.remove('is-active');
          b.classList.toggle('is-done', j < idx);
          if (j === idx) { void b.offsetWidth; b.classList.add('is-active'); }
        });
        if (num) num.textContent = (idx + 1 < 10 ? '0' : '') + (idx + 1);
        clearTimeout(timer);
        if (!reduce && slides.length > 1) timer = setTimeout(function () { show(idx + 1); }, ms);
      }
      bars.forEach(function (b, j) { b.addEventListener('click', function () { show(j); }); });
      var pv = $('[data-rw-salon-prev]', v), nx = $('[data-rw-salon-next]', v);
      if (pv) pv.addEventListener('click', function () { show(idx - 1); });
      if (nx) nx.addEventListener('click', function () { show(idx + 1); });
      var media = $('.rw-salon__media', v), sx = 0;
      if (media) {
        media.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
        media.addEventListener('touchend', function (e) { var dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 50) show(idx + (dx < 0 ? 1 : -1)); });
      }
      show(0);

      var ins = $$('[data-rw-in]', v);
      if (!hasGsap || reduce) { ins.forEach(function (el) { el.style.animation = 'none'; el.style.opacity = 1; }); return; }
      ins.forEach(function (el) { el.style.animation = 'none'; });
      var mark = $('.rw-salon__mark', v);
      var tl = gsap.timeline({ delay: 0.05 });
      if (media) tl.fromTo(media, { clipPath: 'inset(0 0 0 100%)' }, { clipPath: 'inset(0 0 0 0%)', duration: 1.7, ease: 'power4.inOut', clearProps: 'clipPath' }, 0);
      if (mark) tl.fromTo(mark, { opacity: 0, rotate: -24, scale: 0.92 }, { opacity: 1, rotate: 0, scale: 1, duration: 2.6, ease: 'power3.out' }, 0.2);
      tl.fromTo(ins, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out', stagger: 0.12 }, 0.55);
      if (ST) {
        gsap.to($('.rw-salon__slides', v), { yPercent: 8, ease: 'none', scrollTrigger: { trigger: v, start: 'top top', end: 'bottom top', scrub: true } });
        if (mark) gsap.to(mark, { rotate: 30, ease: 'none', scrollTrigger: { trigger: v, start: 'top top', end: 'bottom top', scrub: true } });
      }
    });
  }

  /* ------------------------------------------------------------------------
     Five elements — coverflow
     ------------------------------------------------------------------------ */
  function initFlow() {
    $$('[data-rw-flow]').forEach(function (sec) {
      if (sec.dataset.rwDone) return; sec.dataset.rwDone = '1';
      var cards = $$('.rw-flow__card', sec);
      var items = $$('.rw-flow__item', sec);
      var tabs = $$('.rw-flow__tab', sec);
      var n = cards.length;
      if (!n) return;
      var ms = parseInt(sec.getAttribute('data-interval') || '5000', 10);
      var idx = 0, prev = [], timer, paused = false, inView = false;
      function off(j) { var o = j - idx; if (o > n / 2) o -= n; if (o < -n / 2) o += n; return o; }
      function place() {
        cards.forEach(function (c, j) {
          var o = off(j), a = Math.abs(o);
          if (prev[j] !== undefined && Math.abs(o - prev[j]) > 2) {
            /* wrapping round the back — teleport, then fade in */
            c.style.transition = 'none'; c.style.opacity = '0';
            c.style.setProperty('--o', o); c.style.setProperty('--a', a);
            void c.offsetWidth;
            c.style.transition = ''; c.style.opacity = '';
          } else {
            c.style.setProperty('--o', o); c.style.setProperty('--a', a);
          }
          prev[j] = o;
          c.classList.toggle('is-active', o === 0);
          c.setAttribute('tabindex', o === 0 ? '0' : '-1');
        });
      }
      function show(i) {
        idx = ((i % n) + n) % n;
        var col = cards[idx].getAttribute('data-color');
        if (col) sec.style.setProperty('--el', col);
        place();
        items.forEach(function (it, j) {
          var on = j === idx;
          it.classList.toggle('is-active', on);
          it.setAttribute('aria-hidden', on ? 'false' : 'true');
          $$('a', it).forEach(function (a) { a.setAttribute('tabindex', on ? '0' : '-1'); });
        });
        tabs.forEach(function (t, j) { t.classList.toggle('is-active', j === idx); t.setAttribute('aria-selected', j === idx ? 'true' : 'false'); });
        schedule();
      }
      function schedule() {
        clearTimeout(timer);
        if (!reduce && !paused && inView && n > 1) timer = setTimeout(function () { show(idx + 1); }, ms);
      }
      cards.forEach(function (c, j) {
        c.addEventListener('click', function (e) { if (j !== idx) { e.preventDefault(); show(j); } });
        if (finePointer) c.addEventListener('mousemove', function (e) {
          var r = c.getBoundingClientRect();
          c.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100) + '%');
          c.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100) + '%');
        });
      });
      tabs.forEach(function (t, j) { t.addEventListener('click', function () { show(j); }); });
      var p = $('[data-rw-flow-prev]', sec), nx = $('[data-rw-flow-next]', sec);
      if (p) p.addEventListener('click', function () { show(idx - 1); });
      if (nx) nx.addEventListener('click', function () { show(idx + 1); });
      sec.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { show(idx - 1); } else if (e.key === 'ArrowRight') { show(idx + 1); }
      });
      var stage = $('.rw-flow__stage', sec), sx = 0;
      if (stage) {
        stage.addEventListener('mouseenter', function () { paused = true; clearTimeout(timer); });
        stage.addEventListener('mouseleave', function () { paused = false; schedule(); });
        stage.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; paused = true; clearTimeout(timer); }, { passive: true });
        stage.addEventListener('touchend', function (e) {
          var dx = e.changedTouches[0].clientX - sx; paused = false;
          if (Math.abs(dx) > 40) show(idx + (dx < 0 ? 1 : -1)); else schedule();
        });
      }
      sec.addEventListener('focusin', function () { paused = true; clearTimeout(timer); });
      sec.addEventListener('focusout', function () { paused = false; schedule(); });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (en) { inView = en[0].isIntersecting; schedule(); }, { threshold: 0.35 }).observe(sec);
      } else { inView = true; }
      show(0);

      /* entrance — the deck fans out */
      if (hasGsap && ST && !reduce) {
        sec.classList.add('is-gathered');
        ST.create({
          trigger: stage || sec, start: 'top 82%', once: true,
          onEnter: function () {
            sec.classList.add('is-entering');
            requestAnimationFrame(function () { sec.classList.remove('is-gathered'); });
            setTimeout(function () { sec.classList.remove('is-entering'); }, 2000);
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------------------
     Legacy reveal — pinned window-to-full-bleed (desktop only)
     ------------------------------------------------------------------------ */
  function initLegacy() {
    $$('[data-rw-legacy]').forEach(function (sec) {
      if (sec.dataset.rwDone) return; sec.dataset.rwDone = '1';
      if (!hasGsap || !ST || reduce || !gsap.matchMedia) return;
      var mm = gsap.matchMedia();
      mm.add('(min-width: 990px)', function () {
        var pin = $('.rw-legacy__pin', sec);
        var media = $('.rw-legacy__media', sec);
        var img = $('.rw-legacy__media img', sec);
        var shade = $('.rw-legacy__shade', sec);
        var wl = $('.rw-legacy__word--l', sec), wr = $('.rw-legacy__word--r', sec);
        var ins = $$('.rw-legacy__in', sec);
        var tl = gsap.timeline({ scrollTrigger: { trigger: sec, start: 'top top', end: '+=170%', pin: pin, scrub: 1, anticipatePin: 1 } });
        tl.fromTo(media, { clipPath: 'inset(13% 37.5% 13% 37.5%)' }, { clipPath: 'inset(0% 0% 0% 0%)', ease: 'power2.inOut', duration: 1 }, 0);
        if (img) tl.fromTo(img, { scale: 1.32 }, { scale: 1, ease: 'power2.inOut', duration: 1 }, 0);
        if (wl) tl.fromTo(wl, { opacity: 1, xPercent: 0 }, { opacity: 0, xPercent: -55, ease: 'power2.in', duration: 0.65 }, 0);
        if (wr) tl.fromTo(wr, { opacity: 1, xPercent: 0 }, { opacity: 0, xPercent: 55, ease: 'power2.in', duration: 0.65 }, 0);
        if (shade) tl.fromTo(shade, { opacity: 0 }, { opacity: 1, ease: 'none', duration: 0.45 }, 0.6);
        tl.fromTo(ins, { opacity: 0, y: 40 }, { opacity: 1, y: 0, ease: 'power2.out', stagger: 0.07, duration: 0.45 }, 0.82);
        $$('[data-rw-lcount]', sec).forEach(function (c) {
          var to = parseFloat(c.getAttribute('data-rw-lcount')) || 0;
          var from = parseFloat(c.getAttribute('data-from')) || 0;
          var o = { v: from };
          tl.fromTo(o, { v: from }, {
            v: to, ease: 'power1.out', duration: 0.5,
            onUpdate: function () { var n = Math.round(o.v); c.textContent = from ? String(n) : n.toLocaleString('en-IN'); }
          }, 1.0);
        });
        tl.to({}, { duration: 0.25 });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Reels — play muted only while visible; sound toggle; arrow scroll
     ------------------------------------------------------------------------ */
  function initReels() {
    $$('[data-rw-reels]').forEach(function (sec) {
      if (sec.dataset.rwDone) return; sec.dataset.rwDone = '1';
      var track = $('[data-rw-reels-track]', sec);
      var vids = $$('video', sec);
      vids.forEach(function (v) { v.muted = true; v.setAttribute('muted', ''); v.playsInline = true; v.removeAttribute('controls'); });
      if ('IntersectionObserver' in window && !reduce) {
        var io = new IntersectionObserver(function (en) {
          en.forEach(function (e) {
            var v = e.target;
            if (e.isIntersecting && e.intersectionRatio >= 0.6) { if (v.preload === 'none') v.preload = 'auto'; var pr = v.play(); if (pr && pr.catch) pr.catch(function () {}); }
            else v.pause();
          });
        }, { threshold: [0, 0.6] });
        vids.forEach(function (v) { io.observe(v); });
      }
      $$('[data-rw-reel-sound]', sec).forEach(function (b) {
        b.addEventListener('click', function () {
          var v = b.parentElement.querySelector('video'); if (!v) return;
          var on = v.muted;
          vids.forEach(function (o) { o.muted = true; });
          $$('[data-rw-reel-sound]', sec).forEach(function (o) { o.setAttribute('aria-pressed', 'false'); o.setAttribute('aria-label', 'Turn sound on'); });
          v.muted = !on;
          if (on) { b.setAttribute('aria-pressed', 'true'); b.setAttribute('aria-label', 'Turn sound off'); var pr = v.play(); if (pr && pr.catch) pr.catch(function () {}); }
        });
      });
      function step(dir) {
        var card = $('.rw-reel', track); if (!card) return;
        track.scrollBy({ left: dir * (card.getBoundingClientRect().width + 16) * 2, behavior: 'smooth' });
      }
      var p = $('[data-rw-reels-prev]', sec), n = $('[data-rw-reels-next]', sec);
      if (p) p.addEventListener('click', function () { step(-1); });
      if (n) n.addEventListener('click', function () { step(1); });
    });
  }

  function initElements() {
    $$('[data-rw-tattva]').forEach(function (sec) {
      if (sec.dataset.rwDone) return; sec.dataset.rwDone = '1';
      var float = $('.rw-tattva__float', sec);
      var imgs = float ? $$('img', float) : [];
      var items = $$('.rw-tattva__item', sec);
      if (!float || !finePointer || !hasGsap) return;
      var xTo = gsap.quickTo(float, 'x', { duration: 0.9, ease: 'power3' });
      var yTo = gsap.quickTo(float, 'y', { duration: 0.9, ease: 'power3' });
      gsap.set(float, { xPercent: -50, yPercent: -50, scale: 0.7 });
      sec.addEventListener('mousemove', function (e) { xTo(e.clientX + 40); yTo(e.clientY); });
      items.forEach(function (it) {
        it.addEventListener('mouseenter', function () {
          var i = parseInt(it.getAttribute('data-index'), 10);
          imgs.forEach(function (im, j) { im.classList.toggle('is-active', j === i); });
          float.classList.add('is-on');
          gsap.to(float, { scale: 1, duration: 0.8, ease: 'power3.out', overwrite: 'auto' });
        });
        it.addEventListener('mouseleave', function () { float.classList.remove('is-on'); gsap.to(float, { scale: 0.7, duration: 0.6, ease: 'power3.out', overwrite: 'auto' }); });
      });
    });

    /* Love letters — framed certificate */
    $$('[data-rw-letters2]').forEach(function (sec) {
      if (sec.dataset.rwDone) return; sec.dataset.rwDone = '1';
      var letters = $$('.rw-cert__letter', sec);
      var phases = $$('.rw-cert__phase', sec);
      if (letters.length < 2) return;
      var ms = parseInt(sec.getAttribute('data-interval') || '7000', 10), idx = 0, timer, paused = false;
      function show(i) {
        idx = (i + letters.length) % letters.length;
        letters.forEach(function (l, j) { l.classList.toggle('is-active', j === idx); });
        phases.forEach(function (p, j) { p.classList.toggle('is-active', j === idx); });
        schedule();
      }
      function schedule() { clearTimeout(timer); if (!paused && !reduce) timer = setTimeout(function () { show(idx + 1); }, ms); }
      phases.forEach(function (p, j) { p.addEventListener('click', function () { show(j); }); });
      var prev = $('[data-rw-l2-prev]', sec), next = $('[data-rw-l2-next]', sec);
      if (prev) prev.addEventListener('click', function () { show(idx - 1); });
      if (next) next.addEventListener('click', function () { show(idx + 1); });
      sec.addEventListener('mouseenter', function () { paused = true; clearTimeout(timer); });
      sec.addEventListener('mouseleave', function () { paused = false; schedule(); });
      var stage = $('.rw-cert', sec), sx = 0;
      if (stage) {
        stage.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
        stage.addEventListener('touchend', function (e) { var dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 50) show(idx + (dx < 0 ? 1 : -1)); });
      }
      schedule();
    });
  }

  /* ------------------------------------------------------------------------
     Text decode (scramble) — used by correspondence letters
     ------------------------------------------------------------------------ */
  var GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#*/+=';
  function scramble(el) {
    if (!el) return;
    var final = el.getAttribute('data-rw-final') || el.textContent;
    el.setAttribute('data-rw-final', final);
    if (reduce) { el.textContent = final; return; }
    var frame = 0, total = Math.min(36, 14 + Math.round(final.length / 8));
    clearInterval(el._rwT);
    el._rwT = setInterval(function () {
      frame++;
      var reveal = Math.floor((frame / total) * final.length);
      var out = '';
      for (var i = 0; i < final.length; i++) {
        var ch = final[i];
        out += (i < reveal || ch === ' ') ? ch : GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
      }
      el.textContent = out;
      if (frame >= total) { clearInterval(el._rwT); el.textContent = final; }
    }, 28);
  }
  window.rwScramble = scramble;

  /* ------------------------------------------------------------------------
     Correspondence (love letters)
     ------------------------------------------------------------------------ */
  function initCorr() {
    $$('[data-rw-corr]').forEach(function (sec) {
      if (sec.dataset.rwCorrDone) return; sec.dataset.rwCorrDone = '1';
      var tabs = $$('.rw-corr__row', sec);
      var letters = $$('.rw-corr__letter', sec);
      if (!tabs.length) return;
      var ms = 7000, idx = 0, timer = null, paused = false;
      sec.style.setProperty('--rw-letter-ms', ms + 'ms');
      function show(i, user) {
        idx = (i + tabs.length) % tabs.length;
        tabs.forEach(function (t, j) {
          var on = j === idx;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          var bar = $('.rw-corr__progress', t);
          if (bar) { bar.classList.remove('is-run'); if (on) { void bar.offsetWidth; bar.classList.add('is-run'); } }
        });
        letters.forEach(function (l, j) { l.classList.toggle('is-active', j === idx); });
        scramble($('[data-rw-scramble]', letters[idx]));
        if (user && window.matchMedia('(max-width: 989px)').matches) tabs[idx].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        schedule();
      }
      function schedule() {
        clearTimeout(timer);
        if (paused || reduce) return;
        timer = setTimeout(function () { show(idx + 1); }, ms);
      }
      tabs.forEach(function (t, j) { t.addEventListener('click', function () { show(j, true); }); });
      var prev = $('[data-rw-corr-prev]', sec), next = $('[data-rw-corr-next]', sec);
      if (prev) prev.addEventListener('click', function () { show(idx - 1, true); });
      if (next) next.addEventListener('click', function () { show(idx + 1, true); });
      sec.addEventListener('mouseenter', function () { paused = true; clearTimeout(timer); });
      sec.addEventListener('mouseleave', function () { paused = false; schedule(); });
      var stage = $('.rw-corr__stage', sec), sx = 0;
      if (stage) {
        stage.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
        stage.addEventListener('touchend', function (e) { var dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 50) show(idx + (dx < 0 ? 1 : -1), true); });
      }
      show(0);
    });
  }

  /* ------------------------------------------------------------------------
     Story — scroll-scrubbed word highlight
     ------------------------------------------------------------------------ */
  function initStory() {
    $$('[data-rw-scrub-text]').forEach(function (el) {
      var words = wrapWordsPlain(el);
      if (!hasGsap || !ST || reduce) { words.forEach(function (w) { w.classList.add('is-on'); }); return; }
      ST.create({
        trigger: el, start: 'top 80%', end: 'bottom 45%', scrub: true,
        onUpdate: function (self) {
          var n = Math.round(self.progress * words.length);
          words.forEach(function (w, i) { w.classList.toggle('is-on', i < n); });
        },
      });
    });
  }

  /* ------------------------------------------------------------------------
     Drag-to-scroll rails (desktop) + arrows + progress
     ------------------------------------------------------------------------ */
  function initRails() {
    $$('[data-rw-rail]').forEach(function (rail) {
      var wrap = rail.closest('[data-rw-rail-wrap]') || rail.parentElement;
      var prog = $('.rw-rail-progress span', wrap);
      var prev = $('[data-rw-prev]', wrap);
      var next = $('[data-rw-next]', wrap);
      var step = function () { var c = rail.firstElementChild; return c ? c.getBoundingClientRect().width + 20 : 300; };
      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
      var updateProg = function () {
        if (!prog) return;
        var max = rail.scrollWidth - rail.clientWidth;
        var ratio = rail.clientWidth / rail.scrollWidth;
        prog.style.width = Math.max(ratio * 100, 8) + '%';
        prog.style.transform = 'translateX(' + (max > 0 ? (rail.scrollLeft / max) * ((1 / ratio) - 1) * 100 : 0) + '%)';
      };
      rail.addEventListener('scroll', updateProg, { passive: true });
      window.addEventListener('resize', updateProg);
      updateProg();
      if (!finePointer) return;
      var down = false, startX = 0, startLeft = 0, moved = false;
      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false; startX = e.clientX; startLeft = rail.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 6) { moved = true; rail.classList.add('is-dragging'); }
        rail.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!down) return; down = false;
        setTimeout(function () { rail.classList.remove('is-dragging'); }, 0);
      });
      rail.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    });

    /* Tabbed rails */
    $$('[data-rw-tabs]').forEach(function (wrap) {
      var tabs = $$('[role="tab"]', wrap);
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (t) { t.setAttribute('aria-selected', t === tab ? 'true' : 'false'); });
          $$('[role="tabpanel"]', wrap).forEach(function (p) {
            var on = p.id === tab.getAttribute('aria-controls');
            p.hidden = !on;
            if (on && hasGsap && !reduce) gsap.fromTo(p.querySelectorAll('.rw-card'), { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8, stagger: 0.06, ease: 'power3.out' });
          });
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Magnetic elements + custom cursor
     ------------------------------------------------------------------------ */
  function initCursor() {
    if (!finePointer || reduce || !hasGsap) return;
    var cursor = $('.rw-cursor');
    if (cursor) {
      var dot = $('.rw-cursor__dot', cursor);
      var label = $('.rw-cursor__label', cursor);
      var xTo = gsap.quickTo(cursor, 'x', { duration: 0.35, ease: 'power3' });
      var yTo = gsap.quickTo(cursor, 'y', { duration: 0.35, ease: 'power3' });
      window.addEventListener('mousemove', function (e) { xTo(e.clientX); yTo(e.clientY); });
      document.addEventListener('mouseover', function (e) {
        var view = e.target.closest('[data-cursor="view"]');
        var link = e.target.closest('a, button, label, summary');
        cursor.classList.toggle('is-view', !!view);
        cursor.classList.toggle('is-link', !view && !!link);
        if (view && label) label.textContent = view.getAttribute('data-cursor-label') || 'View';
      });
      document.addEventListener('mouseleave', function () { gsap.to(cursor, { opacity: 0, duration: 0.3 }); });
      document.addEventListener('mouseenter', function () { gsap.to(cursor, { opacity: 1, duration: 0.3 }); });
      if (dot) dot.style.opacity = 1;
    }
    $$('[data-rw-magnetic]').forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-rw-magnetic')) || 0.35;
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        gsap.to(el, { x: (e.clientX - r.left - r.width / 2) * strength, y: (e.clientY - r.top - r.height / 2) * strength, duration: 0.5, ease: 'power3.out' });
      });
      el.addEventListener('mouseleave', function () { gsap.to(el, { x: 0, y: 0, duration: 0.9, ease: 'elastic.out(1, 0.4)' }); });
    });
  }

  /* ------------------------------------------------------------------------
     Footer wordmark rise
     ------------------------------------------------------------------------ */
  function initFooter() {
    var giant = $('.rw-footer__giant');
    if (!giant || !hasGsap || !ST || reduce) return;
    var letters = giant.textContent.trim().split('');
    giant.innerHTML = letters.map(function (l) { return '<span>' + l + '</span>'; }).join('');
    gsap.fromTo($$('span', giant), { yPercent: 100, opacity: 0 }, {
      yPercent: 0, opacity: 1, ease: 'power4.out', duration: 1.2, stagger: 0.05,
      scrollTrigger: { trigger: giant, start: 'top 98%', once: true },
    });
  }

  /* ------------------------------------------------------------------------
     Toast utility
     ------------------------------------------------------------------------ */
  window.rwToast = function (msg) {
    var t = $('.rw-toast');
    if (!t) return;
    $('.rw-toast__msg', t).textContent = msg;
    t.classList.remove('is-on'); void t.offsetWidth; t.classList.add('is-on');
    clearTimeout(window.rwToastT);
    window.rwToastT = setTimeout(function () { t.classList.remove('is-on'); }, 2600);
  };

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function boot() {
    initHeader();
    initMenu();
    initSearch();
    initRails();
    initCursor();
    runPreloader(function () {
      initHero();
      initSalon();
      initFlow();
      initLegacy();
      initReels();
      initReveals();
      initMarquees();
      initElements();
      initCorr();
      initStory();
      initFooter();
      if (ST) {
        if (ST.sort) ST.sort();
        ST.refresh();
        window.addEventListener('load', function () { ST.refresh(); });
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: rebuild triggers when sections reload */
  document.addEventListener('shopify:section:load', function () {
    if (ST) { ST.getAll().forEach(function (t) { t.kill(); }); }
    initHero(); initSalon(); initFlow(); initLegacy(); initReels(); initReveals(); initMarquees(); initElements(); initCorr(); initStory(); initRails();
    if (ST) ST.refresh();
  });
})();
